import React, { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { Sparkle, Send, Mic, X, Loader2, MicOff } from "lucide-react";

const SUGGESTIONS = [
  "Gift idea for someone who loves ceramics?",
  "A thoughtful anniversary message?",
  "What's trending for a new-home gift?",
];

const ConciergeDrawer = ({ open, onClose }) => {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Welcome. I'm your Vision Concierge — tell me who you're gifting, and I'll curate something unforgettable." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const scrollRef = useRef(null);
  const recogRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    const history = messages.filter((m) => m.role !== "system");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setBusy(true);
    try {
      const res = await api.post("/concierge/chat", { message: msg, history });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: e?.response?.data?.detail || "The concierge is momentarily unavailable." }]);
    } finally { setBusy(false); }
  };

  // Voice-to-Curation via Web Speech API (Chrome/Edge). Graceful fallback elsewhere.
  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Voice input isn't supported in this browser. Try Chrome or Edge."); return; }
    if (listening) { recogRef.current?.stop(); setListening(false); return; }
    const r = new SR();
    r.lang = "en-US"; r.interimResults = false; r.maxAlternatives = 1;
    r.onresult = (e) => { setInput(e.results[0][0].transcript); };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recogRef.current = r;
    setListening(true);
    r.start();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70]" data-testid="concierge-drawer">
      <div className="absolute inset-0 bg-[hsl(38_30%_12%_/_0.45)] backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md surface animate-slide-in flex flex-col" style={{ borderRadius: "28px 0 0 28px" }}>
        {/* header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-border/40">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Sparkle className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-serif text-xl leading-tight">Vision Concierge</div>
              <div className="text-[11px] text-muted-foreground">Bespoke gift & curation advice</div>
            </div>
          </div>
          <button data-testid="concierge-close" onClick={onClose} className="p-2 rounded-full hover:bg-muted/50 text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-3" data-testid="concierge-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                m.role === "user" ? "bg-gradient-to-r from-primary to-secondary text-white" : "surface font-serif"}`}>
                {m.content}
              </div>
            </div>
          ))}
          {busy && <div className="flex justify-start"><div className="surface rounded-2xl px-4 py-2.5 text-sm flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> curating…</div></div>}
          {messages.length <= 1 && !busy && (
            <div className="flex flex-wrap gap-2 pt-2" data-testid="concierge-suggestions">
              {SUGGESTIONS.map((sug, i) => (
                <button key={i} onClick={() => send(sug)} className="text-xs surface rounded-full px-3 py-1.5 text-muted-foreground hover:text-foreground hover:border-primary/40">{sug}</button>
              ))}
            </div>
          )}
        </div>

        {/* composer */}
        <div className="px-5 pb-6 pt-3 border-t border-border/40">
          <div className="flex items-center gap-2">
            <button data-testid="concierge-mic" onClick={toggleMic}
              className={`p-3 rounded-2xl transition-colors ${listening ? "bg-red-500/15 text-red-500 gold-pulse-ring" : "surface text-muted-foreground hover:text-foreground"}`}
              title="Voice-to-Curation">
              {listening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>
            <input
              data-testid="concierge-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={listening ? "Listening…" : "Ask your concierge…"}
              className="flex-1 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40"
            />
            <button data-testid="concierge-send" onClick={() => send()} disabled={busy || !input.trim()}
              className="p-3 rounded-2xl bg-gradient-to-r from-primary to-secondary text-white hover:opacity-90 disabled:opacity-50">
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConciergeDrawer;
